import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Clock, Sun, Moon, Zap as Bolt } from "lucide-react";

const ICONS = { "Off-Peak": Moon, "Normal": Sun, "Peak": Bolt };

export default function TodCostCard({ startIso, endIso, refreshTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/energy/cost-tod`, { params: { start: startIso, end: endIso } })
      .then((r) => alive && setData(r.data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [startIso, endIso, refreshTick]);

  if (!data || !data.tod_enabled) return null;

  const slabs = data.slabs || [];
  const totalKwh = slabs.reduce((acc, s) => acc + (s.kwh || 0), 0) || 1;

  return (
    <section className="panel p-5" data-testid="tod-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-base tracking-tight">TOD COST</h3>
        <Clock className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
      </div>

      {data.current_slab && (
        <div className="flex items-baseline gap-3 mb-4 p-3 border border-emerald-500/30 bg-emerald-500/5 rounded-sm">
          <div>
            <div className="label-tech text-emerald-400">CURRENT SLAB</div>
            <div className="font-heading font-semibold text-lg text-white mt-0.5">{data.current_slab.label}</div>
            <div className="font-mono-tech text-[11px] text-zinc-400 mt-0.5">
              {data.current_slab.start} → {data.current_slab.end}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="font-mono-tech text-3xl text-emerald-300">₹{Number(data.current_slab.rate).toFixed(2)}</div>
            <div className="label-tech text-[10px]">PER kWh</div>
          </div>
        </div>
      )}

      <div className="space-y-2 mb-4">
        {slabs.map((s) => {
          const Icon = ICONS[s.label] || Clock;
          const pct = (s.kwh / totalKwh) * 100;
          return (
            <div key={s.label} data-testid={`tod-slab-row-${s.label}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <Icon className="w-3 h-3 text-zinc-400" />
                  <span className="text-white">{s.label}</span>
                  <span className="label-tech text-[10px]">₹{Number(s.rate).toFixed(2)}</span>
                </div>
                <div className="font-mono-tech text-zinc-300">
                  {Number(s.kwh).toFixed(0)} kWh · ₹{Number(s.cost).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="h-1.5 bg-white/5 rounded-sm overflow-hidden">
                <div
                  className="h-full bg-emerald-500/60"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-3 border-t border-white/10 flex items-baseline justify-between">
        <span className="label-tech">TOD TOTAL ({Number(data.total_kwh).toFixed(0)} kWh)</span>
        <span className="font-mono-tech text-2xl text-white">
          ₹{data.tod_cost ? Number(data.tod_cost).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}
        </span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1 font-mono-tech">
        Flat-rate equivalent: ₹{Number(data.flat_cost || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </div>
      {loading ? <div className="label-tech text-[10px] mt-2">RECALCULATING…</div> : null}
    </section>
  );
}
